import React, { useState, useEffect } from "react";
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  Typography,
  FormControlLabel,
  Switch,
  Divider,
  List,
  IconButton,
  Paper,
  Box
} from "@material-ui/core";
import { 
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon
} from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import { toast } from "react-toastify";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import NewStageDialog from "./NewStageDialog";
import EditStageDialog from "./EditStageDialog";
import ConfirmationDialog from "../ConfirmationDialog";

const useStyles = makeStyles(theme => ({
  content: {
    padding: theme.spacing(2),
  },
  section: {
    marginBottom: theme.spacing(3),
  },
  sectionTitle: {
    marginBottom: theme.spacing(2),
  },
  stageItem: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(1),
    padding: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper,
  },
  stageColor: {
    width: 24,
    height: 24,
    borderRadius: "50%",
    marginRight: theme.spacing(2),
  },
  stageName: {
    flexGrow: 1,
  },
  actionButtons: {
    display: "flex",
  },
  noStagesMessage: {
    padding: theme.spacing(2),
    textAlign: "center",
    color: theme.palette.text.secondary,
  },
  addStageButton: {
    marginTop: theme.spacing(1),
  },
  stageBadge: {
    marginLeft: theme.spacing(1),
    padding: theme.spacing(0.5, 1),
    borderRadius: theme.shape.borderRadius,
    fontSize: "0.75rem",
    fontWeight: "bold",
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.primary.contrastText,
  }
}));

const KanbanSettingsDialog = ({ open, onClose, queueId, settings }) => {
  const classes = useStyles();
  
  const [enabled, setEnabled] = useState(false);
  const [autoCreate, setAutoCreate] = useState(false);
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(false);
  
  const [newStageOpen, setNewStageOpen] = useState(false);
  const [editStageOpen, setEditStageOpen] = useState(false);
  const [editingStage, setEditingStage] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deletingStage, setDeletingStage] = useState(null);

  useEffect(() => {
    if (open) {
      setEnabled(settings?.enabled || false);
      setAutoCreate(settings?.autoCreateCards || false);
      fetchStages();
    }
  }, [open, settings]);

  const fetchStages = async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/kanban/stages", {
        params: { queueId }
      });
      
      // Sort stages by position
      const sortedStages = data.sort((a, b) => a.position - b.position);
      setStages(sortedStages);
    } catch (err) {
      console.error(err);
      toast.error(i18n.t("kanban.notifications.stagesLoadError"));
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      setLoading(true);
      
      const settingsData = {
        queueId: parseInt(queueId),
        enabled,
        autoCreateCards: autoCreate
      };
      
      if (settings?.id) {
        await api.put(`/kanban/settings/${settings.id}`, settingsData);
      } else {
        await api.post("/kanban/settings", settingsData);
      }
      
      toast.success(
        enabled 
          ? i18n.t("kanban.notifications.enabled") 
          : i18n.t("kanban.notifications.disabled")
      );
      
      onClose();
    } catch (err) {
      console.error(err);
      toast.error(i18n.t("kanban.notifications.saveError"));
    } finally {
      setLoading(false);
    }
  };

  const handleAddStage = () => {
    setNewStageOpen(true);
  };

  const handleNewStageClose = () => {
    setNewStageOpen(false);
    fetchStages();
  };

  const handleEditStage = (stage) => {
    setEditingStage(stage);
    setEditStageOpen(true);
  };

  const handleEditStageClose = () => {
    setEditStageOpen(false);
    setEditingStage(null);
    fetchStages();
  };

  const handleDeleteStage = (stage) => {
    setDeletingStage(stage);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await api.delete(`/kanban/stages/${deletingStage.id}`);
      
      toast.success(i18n.t("kanban.notifications.stageDeleted"));
      
      fetchStages();
    } catch (err) {
      console.error(err);
      toast.error(i18n.t("kanban.notifications.stageDeleteError"));
    } finally {
      setDeleteConfirmOpen(false);
      setDeletingStage(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
    setDeletingStage(null);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        aria-labelledby="kanban-settings-dialog-title"
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle id="kanban-settings-dialog-title">
          {i18n.t("kanban.settingsDialog.title")}
        </DialogTitle>
        <DialogContent className={classes.content}>
          <div className={classes.section}>
            <Typography variant="h6" className={classes.sectionTitle}>
              {i18n.t("kanban.settingsDialog.general")}
            </Typography>
            
            <FormControlLabel
              control={
                <Switch
                  checked={enabled}
                  onChange={(e) => setEnabled(e.target.checked)}
                  color="primary"
                />
              }
              label={i18n.t("kanban.settingsDialog.enableKanban")}
            />
            
            <FormControlLabel
              control={
                <Switch
                  checked={autoCreate}
                  onChange={(e) => setAutoCreate(e.target.checked)}
                  color="primary"
                  disabled={!enabled}
                />
              }
              label={i18n.t("kanban.settingsDialog.autoCreateCards")}
            />
          </div>
          
          <Divider />
          
          <div className={classes.section}>
            <Typography variant="h6" className={classes.sectionTitle}>
              {i18n.t("kanban.settingsDialog.stages")}
            </Typography>
            
            {stages.length > 0 ? (
              <List disablePadding>
                {stages.map(stage => (
                  <Paper key={stage.id} className={classes.stageItem} elevation={1}>
                    <Box 
                      className={classes.stageColor}
                      bgcolor={stage.color || "#2196f3"}
                    />
                    <div className={classes.stageName}>
                      <Typography variant="body1">
                        {stage.name}
                        {stage.isInitialStage && (
                          <span className={classes.stageBadge}>
                            {i18n.t("kanban.settingsDialog.initialStage")}
                          </span>
                        )}
                        {stage.isFinalStage && (
                          <span className={classes.stageBadge}>
                            {i18n.t("kanban.settingsDialog.finalStage")}
                          </span>
                        )}
                      </Typography>
                    </div>
                    <div className={classes.actionButtons}>
                      <IconButton 
                        size="small" 
                        onClick={() => handleEditStage(stage)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton 
                        size="small"
                        onClick={() => handleDeleteStage(stage)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </div>
                  </Paper>
                ))}
              </List>
            ) : (
              <Paper className={classes.noStagesMessage}>
                <Typography variant="body2">
                  {i18n.t("kanban.settingsDialog.noStages")}
                </Typography>
              </Paper>
            )}
            
            <Button
              variant="outlined"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleAddStage}
              className={classes.addStageButton}
              disabled={!enabled}
            >
              {i18n.t("kanban.settingsDialog.addStage")}
            </Button>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="default">
            {i18n.t("kanban.buttons.cancel")}
          </Button>
          <Button 
            onClick={handleSaveSettings} 
            color="primary"
            disabled={loading}
          >
            {i18n.t("kanban.buttons.save")}
          </Button>
        </DialogActions>
      </Dialog>
      
      <NewStageDialog 
        open={newStageOpen}
        onClose={handleNewStageClose}
        queueId={queueId}
        existingStages={stages}
      />
      
      {editingStage && (
        <EditStageDialog
          open={editStageOpen}
          onClose={handleEditStageClose}
          stage={editingStage}
        />
      )}
      
      <ConfirmationDialog
        open={deleteConfirmOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title={i18n.t("kanban.dialogs.deleteStage.title")}
        message={i18n.t("kanban.dialogs.deleteStage.message")}
      />
    </>
  );
};

export default KanbanSettingsDialog;